import { notification } from 'antd';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import useAPIMutation from '../api/useAPIMutation';
import useAPIQuery from '../api/useAPIQuery';
import { JobPostingDetail } from '../types';
import { buildCandidateQueryParam, buildRecruitmentProcessQueryParam } from '../utils';
import SendCustomTestsModal, {
  SendCustomTestsFormValues,
  SendCustomTestsModalProps,
} from './SendCustomTestsModal';

interface BulkSendCustomTestsModalProps extends Omit<SendCustomTestsModalProps, 'helmetTitle'> {
  jobPostingId?: JobPostingDetail['id'];
  onSuccess?: (
    data: void,
    variables: SendCustomTestsFormValues,
    context: unknown
  ) => void | Promise<unknown>;
  functionId?: JobPostingDetail['function_id'];
  totalCount?: number;
}

const BulkSendCustomTestsModal = ({
  onSuccess,
  jobPostingId,
  functionId,
  totalCount,
  ...props
}: BulkSendCustomTestsModalProps) => {
  const queryClient = useQueryClient();

  const [searchParams] = useSearchParams();

  const user = useAPIQuery('/users/me/');

  const mutation = useAPIMutation(
    async (
      {
        message,
        surveys,
        subject,
        attachments,
        enableReplyTo,
        fromName,
        replyTo,
      }: SendCustomTestsFormValues,
      api
    ) => {
      const attachmentIds: number[] = [];

      for (const attachment of attachments || []) {
        if (attachment.originFileObj) {
          const attachmentFormData = new FormData();
          attachmentFormData.append('filename', attachment.name);
          attachmentFormData.append('file', attachment.originFileObj as File, attachment.name);
          attachmentFormData.append('mimetype', attachment.type as string);
          const attachmentResponse = await api.post('/attachments/', attachmentFormData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          attachmentIds.push(attachmentResponse.data.id);
        } else {
          attachmentIds.push(parseInt(attachment.uid, 10));
        }
      }

      if (jobPostingId) {
        await api.post(
          '/recruitment-processes-actions/send-email-to-all/',
          {
            from_name: fromName,
            job_posting: jobPostingId,
            subject,
            message,
            ...(enableReplyTo ? { reply_to: replyTo } : {}),
            type: 'CUSTOM_TESTS',
            custom_tests_ids: surveys,
            attachments: attachmentIds,
            created_by: user.data?.id,
          },
          {
            params: await buildRecruitmentProcessQueryParam(
              searchParams,
              api,
              jobPostingId,
              functionId
            ),
          }
        );
      } else {
        await api.post(
          '/candidates-actions/send-email-to-all/',
          {
            from_name: fromName,
            subject,
            message,
            ...(enableReplyTo ? { reply_to: replyTo } : {}),
            type: 'CUSTOM_TESTS',
            custom_tests_ids: surveys,
            attachments: attachmentIds,
            created_by: user.data?.id,
          },
          {
            params: buildCandidateQueryParam(searchParams),
          }
        );
      }
    },
    {
      onSuccess,
      onSettled: async () => {
        await queryClient.invalidateQueries('/candidate-history/');
      },
      onError: (err, variables, context) => {
        if (err?.response?.data?.error === 'empty_queryset_send_email_to_all') {
          return notification.error({
            duration: 0,
            message: 'Erro',
            description:
              'Não é possível realizar esta ação, pois não há candidatos a serem selecionados. Recarregue a página e tente novamente.',
          });
        }

        return queryClient.defaultMutationOptions().onError?.(err, variables, context);
      },
    }
  );

  return (
    <SendCustomTestsModal
      title={
        <span>
          Enviar testes customizados para <strong>{totalCount ?? '...'} candidato(s)</strong>
        </span>
      }
      jobPostingId={jobPostingId}
      helmetTitle={`Enviar testes customizados para ${totalCount ?? '...'} candidato(s)`}
      loading={mutation.isLoading}
      onFinish={(variables) => {
        mutation.mutate(variables);
      }}
      {...props}
    />
  );
};

export default BulkSendCustomTestsModal;
